import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A white screen is the worst possible failure mode for software someone has
 * unsaved work in. If the editor throws, this catches it, says what happened,
 * and — crucially — tells the user their project is still in local storage and
 * how to get back to it.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[stage-zero] the editor crashed', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 32, textAlign: 'center' }}>
        <div style={{ maxWidth: 560, display: 'grid', gap: 12 }}>
          <h1 style={{ fontSize: 16, margin: 0 }}>Stage Zero hit an unexpected error</h1>
          <p style={{ color: 'var(--text-2)', margin: 0, lineHeight: 1.7 }}>
            Your projects and audio are still stored on this computer and were not affected. Reloading the page
            reopens your last project, and any unsaved work is offered as a recovery point.
          </p>
          <pre
            style={{
              textAlign: 'left',
              background: 'var(--bg-2)',
              border: '1px solid var(--line)',
              borderRadius: 4,
              padding: 10,
              fontSize: 11,
              overflow: 'auto',
              maxHeight: 180,
              color: 'var(--text-2)',
            }}
          >
            {error.message}
          </pre>
          <div>
            <button className="btn btn--primary" onClick={() => window.location.reload()}>
              Reload Stage Zero
            </button>
          </div>
        </div>
      </div>
    );
  }
}
